import React, {
  useState,
  useRef,
  createContext,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { Box, Button, styled } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { isExternalUrl } from '@leagueplatform/web-common';
import { useMatch } from '@leagueplatform/routing';
import { NavLink } from 'hooks/use-nav-links-list.hook';
import { AppNavLink } from 'components/header-nav/app-nav-link.component';
import { AppBarNavLink } from '@leagueplatform/web-common-components';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
// Context for coordinating submenu states across multiple instances
type SubmenuContextType = {
  currentFocusedSubmenu: string | null;
  currentHoveredSubmenu: string | null;
  setFocusedSubmenu: (id: string | null) => void;
  setHoveredSubmenu: (id: string | null) => void;
  clearAllSubmenus: () => void;
};

const SubmenuContext = createContext<SubmenuContextType | undefined>(undefined);

export const SubmenuProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentFocusedSubmenu, setCurrentFocusedSubmenu] = useState<
    string | null
  >(null);
  const [currentHoveredSubmenu, setCurrentHoveredSubmenu] = useState<
    string | null
  >(null);

  const setFocusedSubmenu = useCallback(
    (id: string | null) => {
      setCurrentFocusedSubmenu(id);
      // When focusing on a submenu, clear any hovered submenu
      if (id && currentHoveredSubmenu && currentHoveredSubmenu !== id) {
        setCurrentHoveredSubmenu(null);
      }
    },
    [currentHoveredSubmenu],
  );

  const setHoveredSubmenu = useCallback(
    (id: string | null) => {
      // Only set hovered if there's no focused submenu, or if it's the same submenu
      if (!currentFocusedSubmenu || currentFocusedSubmenu === id) {
        setCurrentHoveredSubmenu(id);
      }
    },
    [currentFocusedSubmenu],
  );

  const clearAllSubmenus = useCallback(() => {
    setCurrentFocusedSubmenu(null);
    setCurrentHoveredSubmenu(null);
  }, []);

  const value = useMemo(
    () => ({
      currentFocusedSubmenu,
      currentHoveredSubmenu,
      setFocusedSubmenu,
      setHoveredSubmenu,
      clearAllSubmenus,
    }),
    [
      currentFocusedSubmenu,
      currentHoveredSubmenu,
      setFocusedSubmenu,
      setHoveredSubmenu,
      clearAllSubmenus,
    ],
  );

  return (
    <SubmenuContext.Provider value={value}>{children}</SubmenuContext.Provider>
  );
};

const useSubmenuContext = () => {
  const context = useContext(SubmenuContext);
  if (!context) {
    throw new Error('useSubmenuContext must be used within a SubmenuProvider');
  }
  return context;
};

type AppNavSubmenuToggleProps = {
  navLink: NavLink;
  subMenuIsOpen: boolean;
  isActive: boolean;
  handleBlur: React.FocusEventHandler<HTMLElement>;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  onToggleDropdown: () => void;
  onNavLinkClick: () => void;
};
type SubmenuDropdownProps = {
  children: React.ReactNode;
  subMenuIsOpen: boolean;
  onBlur: React.FocusEventHandler<HTMLElement>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

const SubmenuToggle = ({
  navLink,
  subMenuIsOpen,
  isActive,
  handleBlur,
  handleMouseEnter,
  handleMouseLeave,
  onToggleDropdown,
  onNavLinkClick,
}: AppNavSubmenuToggleProps) => {
  const { formatMessage } = useIntl();
  const navLinkLabel = formatMessage({ id: navLink.message_id });

  return (
    <Box
      css={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        gap: 0,
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          bottom: '-1px',
          insetInlineStart: 0,
          width: '100%',
          height: '$quarter',
          backgroundColor: isActive
            ? '$interactiveActionPrimary'
            : 'transparent',
          borderRadius: '$large',
        },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AppBarNavLink
        to={navLink.to}
        key={navLink.message_id}
        color="onSurface.text.primary"
        hoverStyle={{ color: 'interactive.action.primary' }}
        decorativeBarColor="transparent"
        display="flex"
        justifyContent="center"
        alignItems="center"
        paddingX="quarter"
        listItemProps={{
          justifyContent: 'center',
          as: 'div',
        }}
        onBlur={handleBlur}
        onClick={onNavLinkClick}
      >
        <Box css={{ justifyContent: 'center' }}>{navLinkLabel}</Box>
      </AppBarNavLink>
      <Button
        priority="tertiary"
        quiet
        size="small"
        icon={subMenuIsOpen ? 'interfaceChevronUp' : 'interfaceChevronDown'}
        hideLabel
        aria-haspopup="dialog"
        aria-expanded={subMenuIsOpen}
        aria-controls={`${navLink.message_id}_menu`}
        onClick={onToggleDropdown}
        onBlur={handleBlur}
        css={{
          padding: '$quarter',
          '&:focus-visible': { outlineOffset: '$quarter' },
        }}
      >
        {navLinkLabel}
      </Button>
    </Box>
  );
};
/**
 * The dropdown container for the submenu.
 * @param children - The children to display in the dropdown.
 * @param subMenuIsOpen - Whether the submenu is open.
 * @param onBlur - The function to call when the dropdown is blurred.
 * @param onMouseEnter - The function to call when the mouse enters the dropdown.
 * @param onMouseLeave - The function to call when the mouse leaves the dropdown.
 */
const SubmenuDropdown = ({
  children,
  subMenuIsOpen,
  onBlur,
  onMouseEnter,
  onMouseLeave,
}: SubmenuDropdownProps) => (
  <Box
    css={{
      transitionProperty: 'opacity',
      transitionDuration: '250ms',
      animationTimingFunction: 'ease',
      position: 'absolute',
      top: '100%',
      insetInlineStart: '10px',
      border: '$thin',
      backgroundColor: '$surfaceBackgroundSecondary',
      boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
      zIndex: 999,
      minWidth: '200px',
      opacity: subMenuIsOpen ? '1' : '0',
      display: subMenuIsOpen ? 'flex' : 'none',
      gap: '$four',
      padding: '$oneAndHalf $one',
    }}
    onBlur={onBlur}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </Box>
);

export const DropdownColumn = styled(Box, {
  display: 'flex',
  flexDirection: 'column',
});

export const DropdownList = styled('ul', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$one',
  listStyle: 'none',
});

type DropdownListItemProps = {
  navLink: NavLink;
  onNavLinkClick: () => void;
};

/**
 * The navigation item displayed in the submenu.
 * @param navLink - The navigation link data.
 * @param onNavLinkClick - The function to call when the nav link is clicked.
 */
export const DropdownListItem = ({
  navLink,
  onNavLinkClick,
}: DropdownListItemProps) => {
  const isExternal = isExternalUrl(navLink.to);
  return (
    <Box
      as="li"
      css={{
        color: '$onSurfaceTextPrimary',
        fontSize: '$bodyTwo',
      }}
    >
      {isExternal ? (
        <a
          href={navLink.to as string}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavLinkClick}
        >
          {navLink?.text || navLink.message_id}
        </a>
      ) : (
        <Box onClick={onNavLinkClick}>
          <AppNavLink navLink={navLink} />
        </Box>
      )}
    </Box>
  );
};

export const AppNavSubmenu = ({ navLink }: { navLink: NavLink }) => {
  const submenuRef = useRef<HTMLLIElement>(null);
  const {
    currentFocusedSubmenu,
    currentHoveredSubmenu,
    setFocusedSubmenu,
    setHoveredSubmenu,
    clearAllSubmenus,
  } = useSubmenuContext();

  // Generate unique ID for this submenu
  const submenuId = `submenu-${navLink.message_id}`;

  // Determine if this submenu should be open based on context state
  const subMenuIsOpen =
    currentFocusedSubmenu === submenuId || currentHoveredSubmenu === submenuId;

  const match = useMatch({ path: navLink.to as string, end: false });
  const isActive = match !== null;

  const handleMouseEnter = () => {
    setHoveredSubmenu(submenuId);
  };

  const handleMouseLeave = () => {
    if (currentHoveredSubmenu === submenuId) {
      setHoveredSubmenu(null);
    }
  };

  const handleToggle = () => {
    if (subMenuIsOpen) {
      clearAllSubmenus();
    } else {
      setFocusedSubmenu(submenuId);
    }
  };

  const handleNavLinkClick = (navlink: NavLink) => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, navlink.analytics);
    clearAllSubmenus();
  };

  const hasSubMenuItems = Boolean(navLink?.subMenu?.items.length);

  const handleBlur: React.FocusEventHandler<HTMLElement> = (event) => {
    const nextFocused = event.relatedTarget as Node | null;
    if (!nextFocused || !submenuRef.current?.contains(nextFocused)) {
      clearAllSubmenus();
    }
  };

  return (
    <Box
      ref={submenuRef}
      as="li"
      css={{
        listStyle: 'none',
        flexGrow: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <SubmenuToggle
        handleBlur={handleBlur}
        navLink={navLink}
        subMenuIsOpen={subMenuIsOpen}
        isActive={isActive}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        onToggleDropdown={handleToggle}
        onNavLinkClick={() => handleNavLinkClick(navLink)}
      />
      <SubmenuDropdown
        onBlur={handleBlur}
        subMenuIsOpen={subMenuIsOpen && hasSubMenuItems}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <DropdownColumn>
          <DropdownList id={`${navLink.message_id}_menu`}>
            {navLink?.subMenu?.items.map((item) => (
              <DropdownListItem
                key={item.message_id}
                navLink={item}
                onNavLinkClick={() => handleNavLinkClick(item)}
              />
            ))}
          </DropdownList>
        </DropdownColumn>
      </SubmenuDropdown>
    </Box>
  );
};
